import { useMemo, useState } from 'react';
import {
  CalendarClockIcon,
  CircleCheckIcon,
  CirclePauseIcon,
  LogInIcon,
  TriangleAlertIcon,
  UsersIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { forgeName, statusWords } from '@/features/identity';
import { useWorkspaceList } from '@/features/workspaces';
import { HeldCountdown } from '@/shared/components/HeldCountdown';
import { EyebrowLabel } from '@/shared/components/EyebrowLabel';
import {
  LIST_ROW_PHONE_GRID,
  LIST_ROW_PHONE_NAME,
  LIST_ROW_PHONE_SECOND,
  LIST_ROW_PHONE_TEXT,
} from '@/shared/components/listRow';
import { formatDurationWords } from '@/shared/utils/time';

import {
  needsALook,
  joinNames,
  namesOf,
  remindedRecently,
  type Look,
  type PersonLook,
} from '../commitCoverage';
import { useForgeApps } from '../hooks/useForgeApps';
import { useHeldReminders, type HeldReminder } from '../hooks/useHeldReminders';
import { usePeopleDirectory } from '../hooks/useLinkedAccounts';
import {
  COMMIT_FIXES_COPY as FIXES,
  COMMIT_SUMMARY_COPY as WORDS,
  FORGE_APPS_COPY,
} from '../linkedAccountsCopy';

/** How many rows show before "Show all". */
const FIRST = 5;
const ALL_WORKSPACES = 200;

const KIND_ICON: Record<Look['kind'], typeof CirclePauseIcon> = {
  blocked: CirclePauseIcon,
  broken: TriangleAlertIcon,
  expiring: CalendarClockIcon,
  'sign-in': LogInIcon,
  shared: UsersIcon,
};

const KIND_RAIL: Record<Look['kind'], string> = {
  blocked: 'bg-state-danger',
  broken: 'bg-state-danger',
  expiring: 'bg-state-warning',
  'sign-in': 'bg-state-info',
  shared: 'bg-state-warning',
};

const KIND_ICON_COLOUR: Record<Look['kind'], string> = {
  blocked: 'text-state-danger',
  broken: 'text-state-danger',
  expiring: 'text-state-warning',
  'sign-in': 'text-state-info',
  shared: 'text-state-warning',
};

/**
 * Needs a look (spec 164 US2): who is stopped, whose link broke, whose token
 * is about to end, and where people can only paste a token, worst first.
 * Each person can be reminded from their row; the reminder waits six seconds
 * with Undo before it goes.
 */
export function NeedsALook({
  onSetUp,
}: {
  /** Open sign-in setup at a host's form (spec 165). */
  onSetUp: (host: string) => void;
}) {
  const directory = usePeopleDirectory();
  const apps = useForgeApps();
  const workspaces = useWorkspaceList({ limit: ALL_WORKSPACES });
  const reminders = useHeldReminders();
  const [now] = useState(() => new Date());
  const [expanded, setExpanded] = useState(false);

  const names = useMemo(
    () =>
      new Map(
        (workspaces.data?.items ?? []).map((workspace) => [
          workspace.id,
          workspace.name,
        ]),
      ),
    [workspaces.data?.items],
  );
  const data = directory.data;
  const looks = useMemo(
    () =>
      data ? needsALook(data.items, data.hosts, apps.data ?? null, now) : [],
    [data, apps.data, now],
  );

  if (!data) return null;
  const shown = expanded ? looks : looks.slice(0, FIRST);

  return (
    <section
      aria-labelledby="commit-identity-needs-a-look"
      className="flex min-w-0 flex-col gap-3"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="commit-identity-needs-a-look">
          <EyebrowLabel>{WORDS.needsALook}</EyebrowLabel>
        </h2>
        {looks.length > 0 ? (
          <span className="text-muted-foreground text-xs tabular-nums">
            {looks.length}
          </span>
        ) : null}
      </div>

      {looks.length === 0 ? (
        <p className="text-muted-foreground flex items-center gap-2 text-sm">
          <CircleCheckIcon
            aria-hidden="true"
            className="text-state-success size-4"
          />
          {WORDS.allClear}
        </p>
      ) : (
        <ul className="border-border bg-card divide-border flex flex-col divide-y overflow-hidden rounded-md border">
          {shown.map((look) => (
            <LookRow
              key={look.key}
              look={look}
              names={names}
              now={now}
              held={reminders.held[look.key]}
              onRemind={reminders.hold}
              onUndo={reminders.undo}
              onClear={reminders.clear}
              onSetUp={onSetUp}
            />
          ))}
        </ul>
      )}

      {looks.length > FIRST ? (
        <Button
          variant="link"
          size="sm"
          className="h-auto self-start px-0"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? WORDS.showFewer : WORDS.showAll(looks.length)}
        </Button>
      ) : null}

      <p role="status" className="sr-only">
        {reminders.said}
      </p>
    </section>
  );
}

function LookRow({
  look,
  names,
  now,
  held,
  onRemind,
  onUndo,
  onClear,
  onSetUp,
}: {
  look: Look;
  names: ReadonlyMap<string, string>;
  now: Date;
  held: HeldReminder | undefined;
  onRemind: (look: PersonLook) => void;
  onUndo: (key: string) => void;
  onClear: (key: string) => void;
  onSetUp: (host: string) => void;
}) {
  const Icon = KIND_ICON[look.kind];
  const { title, why } = wordsFor(look, names);

  return (
    // On a phone the action sits on the title's line and the reason spans
    // the row under both (docs/phone.md PH-06, spec 280).
    <li
      className={cn(
        'relative flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5 ps-4 pe-3',
        LIST_ROW_PHONE_GRID,
      )}
    >
      <span
        aria-hidden="true"
        className={`absolute inset-y-0 left-0 w-[3px] ${KIND_RAIL[look.kind]}`}
      />
      <Icon
        aria-hidden="true"
        className={`size-4 shrink-0 ${KIND_ICON_COLOUR[look.kind]}`}
      />
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col gap-0.5',
          LIST_ROW_PHONE_TEXT,
        )}
      >
        <p
          className={cn(
            'text-foreground text-sm font-medium [overflow-wrap:anywhere]',
            LIST_ROW_PHONE_NAME,
          )}
        >
          {title}
        </p>
        <p
          className={cn(
            'text-muted-foreground text-xs [overflow-wrap:anywhere]',
            LIST_ROW_PHONE_SECOND,
          )}
        >
          {why}
        </p>
      </div>
      <div
        // No overhang (`LIST_ROW_PHONE_END`): an outlined button's edge
        // would cross the reason under it.
        className="flex shrink-0 items-center gap-2 max-md:col-start-3 max-md:row-start-1"
      >
        {look.kind === 'shared' ? null : look.kind === 'sign-in' ? (
          <Button
            variant="outline"
            size="sm"
            aria-label={FORGE_APPS_COPY.setUpLabel(look.host)}
            onClick={() => onSetUp(look.host)}
          >
            {WORDS.setUp}
          </Button>
        ) : (
          <RemindControl
            look={look}
            now={now}
            held={held}
            onRemind={onRemind}
            onUndo={onUndo}
            onClear={onClear}
          />
        )}
      </div>
    </li>
  );
}

function RemindControl({
  look,
  now,
  held,
  onRemind,
  onUndo,
  onClear,
}: {
  look: PersonLook;
  now: Date;
  held: HeldReminder | undefined;
  onRemind: (look: PersonLook) => void;
  onUndo: (key: string) => void;
  onClear: (key: string) => void;
}) {
  if (held && (held.state !== 'sent' || held.outcome === 'sent')) {
    return (
      <HeldCountdown
        action={held}
        title={look.person}
        onUndo={() => onUndo(look.key)}
      />
    );
  }
  if (held?.outcome && held.outcome !== 'failed') {
    return (
      <p className="text-muted-foreground text-xs">
        {held.outcome === 'too-soon' ? WORDS.tooSoon : WORDS.notNeeded}
      </p>
    );
  }
  if (remindedRecently(look.remindedAt, now)) {
    return (
      <p className="text-muted-foreground text-xs">
        {WORDS.remindedAgo(formatDurationWords(look.remindedAt, now.getTime()))}
      </p>
    );
  }
  return (
    <>
      {held?.outcome === 'failed' ? (
        <p role="alert" className="text-state-danger text-xs">
          {WORDS.remindFailed}
        </p>
      ) : null}
      <Button
        variant="outline"
        size="sm"
        aria-label={WORDS.remindLabel(look.person, look.host)}
        onClick={() => {
          if (held) onClear(look.key);
          onRemind(look);
        }}
      >
        {WORDS.remind}
      </Button>
    </>
  );
}

function wordsFor(
  look: Look,
  names: ReadonlyMap<string, string>,
): { title: string; why: string } {
  if (look.kind === 'sign-in') {
    return { title: WORDS.signIn(look.host), why: WORDS.signInWhy };
  }
  if (look.kind === 'shared') {
    return {
      title: FIXES.shared(look.username, look.people.length, look.host),
      why: FIXES.sharedWhy(joinNames(look.people)),
    };
  }
  const forge = forgeName(look.provider);
  switch (look.kind) {
    case 'blocked': {
      const named = namesOf(look.workspaceIds, names);
      const where =
        named.length === look.workspaceIds.length && named.length > 0
          ? named.join(', ')
          : WORDS.workspaces(look.workspaceIds.length);
      return {
        title: WORDS.blocked(look.person, where),
        why: WORDS.blockedWhy(forge, look.host, look.link !== null),
      };
    }
    case 'broken':
      return {
        title: WORDS.broken(look.person, forge),
        why: WORDS.brokenWhy(
          look.host,
          look.link ? statusWords(look.link) : '',
        ),
      };
    case 'expiring':
      return {
        title: WORDS.expiring(look.person, forge, look.days),
        why: WORDS.expiringWhy(look.host),
      };
  }
}
